import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { NumericFormat } from "react-number-format";

import { currentTenant, thousandsdisplay, userid } from "../../Common/Credentials.js";
import { spin } from "../../Common/NewLoader.jsx";
import { appmasterid } from "../../Common/Permissions.js";
import SupplierService from "./SupplierService.js";

import EditIcon from "../../images/Icons/edit.png";
import DeleteIcon from "../../images/Icons/delete.png";
import AddUser from "../../images/Icons/add-user.png";
import ReturnIcon from "../../images/Icons/return.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

const SupplierDataTable = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'companyname', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Format phone number with country code
  const formatPhoneNumber = (supplier) => {
    if (!supplier) return "";
    const phoneCode = supplier.phonecode || "";
    const contactNumber = supplier.contactnumber || "";
    return `+${phoneCode} ${contactNumber}`;
  };

  // Format currency with NumericFormat
  const formatCurrency = (value) => (
    <NumericFormat
      displayType={"text"}
      value={value || 0}
      decimalScale={2}
      fixedDecimalScale={true}
      thousandSeparator={true}
      thousandsGroupStyle={thousandsdisplay}
      className="UI-currency-value"
    />
  );

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshSupplier = useCallback(() => {
    setLoading(true);

    SupplierService.retriveAllSuppliers(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => a.companyname?.localeCompare(b.companyname));
        setSuppliers(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshSupplier();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshSupplier]);

  useEffect(() => {
    updateFilteredData();
  }, [suppliers, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'customsupplierid':
          aValue = a.customsupplierid || '';
          bValue = b.customsupplierid || '';
          break;
        case 'companyname':
          aValue = a.companyname || '';
          bValue = b.companyname || '';
          break;
        case 'contactname':
          aValue = a.contactname || '';
          bValue = b.contactname || '';
          break;
        case 'contactnumber':
          aValue = a.contactnumber || '';
          bValue = b.contactnumber || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'cityname':
          aValue = a.cityname || '';
          bValue = b.cityname || '';
          break;
        case 'openingbalance':
          aValue = parseFloat(a.openingbalance) || 0;
          bValue = parseFloat(b.openingbalance) || 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = suppliers;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = suppliers.filter(supplier =>
        supplier.customsupplierid?.toLowerCase().includes(lowerSearchTerm) ||
        supplier.companyname?.toLowerCase().includes(lowerSearchTerm) ||
        supplier.contactname?.toLowerCase().includes(lowerSearchTerm) ||
        supplier.contactnumber?.toLowerCase().includes(lowerSearchTerm) ||
        supplier.email?.toLowerCase().includes(lowerSearchTerm) ||
        supplier.cityname?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteSupplier = (id, companyName) => {
    Swal.fire({
      title: `Delete ${companyName}?`,
      text: "Are you sure you want to delete this supplier?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        SupplierService.deleteSupplier(id)
          .then(() => {
            Swal.fire({
              title: "Supplier deleted successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshSupplier();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  const createCustomer = (id, companyName) => {
    const data = {
      id: id,
      tenantid: currentTenant,
      createdby: userid
    };

    Swal.fire({
      title: `Create customer with ${companyName} details?`,
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        SupplierService.addCustomerfromsupplier(data)
          .then(() => {
            Swal.fire({
              title: "Customer Added Successfully!",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => refreshSupplier());
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: `${e.response?.data || "An error occurred"}`,
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  const returnOpeningBalance = (id, companyName) => {
    const data = {
      partyid: id,
      ledgername: "",
      ledgerid: "",
      amount: "",
      tenantId: currentTenant,
      createdBy: userid
    };

    Swal.fire({
      title: `Return opening balance for ${companyName}?`,
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        SupplierService.addPurchaseBalRev(data)
          .then(() => {
            Swal.fire({
              title: "Opening balance returned successfully!",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => refreshSupplier());
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: `${e.response?.data || "An error occurred"}`,
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} suppliers
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No suppliers found matching your search' : 'No suppliers available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'customsupplierid' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('customsupplierid')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Supplier ID
                    <SortIndicator columnKey="customsupplierid" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'companyname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('companyname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Company
                    <SortIndicator columnKey="companyname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'contactname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('contactname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Contact
                    <SortIndicator columnKey="contactname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'contactnumber' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('contactnumber')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Contact Number
                    <SortIndicator columnKey="contactnumber" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'email' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('email')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Email
                    <SortIndicator columnKey="email" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'cityname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('cityname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    City
                    <SortIndicator columnKey="cityname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'openingbalance' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('openingbalance')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Opening Balance
                    <SortIndicator columnKey="openingbalance" />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((supplier) => (
                <tr
                  key={supplier.id}
                  className={`UI-table-row ${hoveredRow === supplier.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(supplier.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Supplier ID */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === supplier.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(supplier.customsupplierid, searchTerm)}
                  </td>

                  {/* Company Name */}
                  <td
                    className={`UI-td ${hoveredRow === supplier.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(supplier.companyname, searchTerm)}
                  </td>

                  {/* Contact Name */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(supplier.contactname, searchTerm)}
                  </td>

                  {/* Contact Number */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(formatPhoneNumber(supplier), searchTerm)}
                  </td>

                  {/* Email */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(supplier.email, searchTerm)}
                  </td>

                  {/* City */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(supplier.cityname, searchTerm)}
                  </td>

                  {/* Opening Balance */}
                  <td
                    className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {formatCurrency(supplier.openingbalance)}
                  </td>

                  {/* Actions Cell */}
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons !gap-0">
                        {/* Edit Supplier */}
                        <Tooltip title="Edit Supplier" arrow slotProps={{
                          popper: {
                            sx: {
                              pointerEvents: 'none',
                            },
                          },
                        }}>
                          <button
                            onClick={() => navigate("/newsupplier", {
                              state: {
                                id: supplier.id,
                                name: "Update Supplier",
                              },
                            })}
                            className={`UI-action-button  ${hoveredAction === `edit-${supplier.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`edit-${supplier.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${supplier.id}` ? 'UI-action-icon-hover' : ''}`}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </button>
                        </Tooltip>

                        {/* Delete Supplier */}
                        {supplier.inuse === "NO" && 
                         (!supplier.openingbalance || supplier.openingbalance === 0) && 
                         (!supplier.ledgerid || supplier.ledgerid === "") && (
                          <Tooltip title="Delete Supplier" arrow slotProps={{
                            popper: {
                              sx: {
                                pointerEvents: 'none',
                              },
                            },
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSupplier(supplier.id, supplier.companyname);
                              }}
                              className={`UI-action-button  ${hoveredAction === `delete-${supplier.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`delete-${supplier.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <img
                                src={DeleteIcon}
                                alt="Delete"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${supplier.id}` ? 'UI-action-icon-hover' : ''}`}
                                style={{ width: "20px", height: "20px" }}
                              />
                            </button>
                          </Tooltip>
                        )}

                        {/* Create Customer */}
                        <Tooltip title="Create Customer" arrow slotProps={{
                          popper: {
                            sx: {
                              pointerEvents: 'none',
                            },
                          },
                        }}>
                          <button
                            onClick={() => createCustomer(supplier.id, supplier.companyname)}
                            className={`UI-action-button  ${hoveredAction === `customer-${supplier.id}` ? 'UI-view-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`customer-${supplier.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={AddUser}
                              alt="Create Customer"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `customer-${supplier.id}` ? 'UI-action-icon-hover' : ''}`}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </button>
                        </Tooltip>

                        {/* Return Opening Balance */}
                        {supplier.openingbalance && supplier.openingbalance !== 0 && (
                          <Tooltip title="Return Opening Balance" arrow slotProps={{
                            popper: {
                              sx: {
                                pointerEvents: 'none',
                              },
                            },
                          }}>
                            <button
                              onClick={() => returnOpeningBalance(supplier.id, supplier.companyname)}
                              className={`UI-action-button  ${hoveredAction === `return-${supplier.id}` ? 'UI-reset-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`return-${supplier.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <img
                                src={ReturnIcon}
                                alt="Return Opening Balance"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `return-${supplier.id}` ? 'UI-action-icon-hover' : ''}`}
                                style={{ width: "20px", height: "20px" }}
                              />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <div className="">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
            <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Suppliers",path:null}]}/>
              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Suppliers
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage supplier records and details
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search suppliers..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search suppliers..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Supplier button */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <Link
                        to="/newsupplier"
                        state={{ id: -1, name: "New Supplier" }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Supplier"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDataTable;